import { FormikHelpers, useFormik } from 'formik';
import {
	Dialog,
	DialogSurface,
	DialogBody,
	DialogContent,
	Input,
	Field,
	makeStyles
} from '@fluentui/react-components';
import SignUpTitle from './SignUpTitle';
import SignUpActions from './SignUpActions';
import { SetStateAction, useEffect } from 'react';
import { SignUpFormState } from './Types';
import { SignUpValidationSchema } from './SignUpValidation';
import {
	PersonRegular,
	MailRegular,
	PasswordRegular
} from '@fluentui/react-icons';
import useMediaQuery from '../Hooks/useMediaQuery';
import {
	useCreateLoginMutation,
	useLoginMutation
} from '../Api/User/userSlice';
import { useAppDispatch } from '../pgStore';
import { login } from '../User/userSlice';

interface SignUpFromProps {
	modalIsOpen: boolean;
	toggleModal: React.Dispatch<SetStateAction<boolean>>;
}

export default function SignUpForm(props: SignUpFromProps) {
	const styles = useStyles();
	const { isMobile } = useMediaQuery();
	const [
		addPlayer,
		{
			isLoading: addingPlayer,
			isSuccess: addIsSuccess,
			isError: addIsError,
			error: addError
		}
	] = useCreateLoginMutation();
	const [
		tryLogin,
		{
			isLoading: loggingIn,
			data: loginResult,
			isSuccess: loginSuccess,
			isError: loginError
		}
	] = useLoginMutation();
	const dispatch = useAppDispatch();

	const initialValues: SignUpFormState = {
		username: '',
		password: '',
		emailAddress: ''
	};
	function submitForm(
		state: SignUpFormState,
		formikHelpers: FormikHelpers<SignUpFormState>
	) {
		void addPlayer(state);
		formikHelpers.setSubmitting(false);
	}

	const formik = useFormik<SignUpFormState>({
		initialValues: initialValues,
		validationSchema: SignUpValidationSchema,
		onSubmit: submitForm
	});

	useEffect(() => {
		if (addIsSuccess) {
			void tryLogin({
				username: formik.values.username,
				password: formik.values.password
			});
		}
	}, [
		addIsSuccess,
		tryLogin,
		formik.values.username,
		formik.values.password
	]);

	useEffect(() => {
		if (loginSuccess) {
			dispatch(login(loginResult));
		}
	}, [loginSuccess, loginResult, dispatch]);

	return (
		<Dialog
			open={props.modalIsOpen}
			onOpenChange={(_event: unknown, data: { open: boolean }) => {
				props.toggleModal(data.open);
			}}
		>
			<DialogSurface className={isMobile ? styles.mobile : ''}>
				<DialogBody>
					<SignUpTitle />

					<DialogContent>
						{addIsError && (
							<span style={{ color: 'red' }}>
								{'data' in addError
									? String(addError.data)
									: 'message' in addError
									? addError.message
									: 'An Error Occurred trying to create the login, please refresh and try again.'}
							</span>
						)}
						{loginError && (
							<span style={{ color: 'red' }}>
								Your account was created but the automatic login
								failed, please close the window and try to login
								manually.
							</span>
						)}
						<form
							id='sign-up-form'
							onSubmit={formik.handleSubmit}
						>
							<Field
								label='Username'
								validationState={
									formik.touched.username &&
									formik.errors.username
										? 'error'
										: 'none'
								}
								validationMessage={formik.errors.username}
								className={styles.field}
							>
								<Input
									id='username'
									name='username'
									value={formik.values.username}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									placeholder='Required'
									contentBefore={<PersonRegular />}
									size='large'
								/>
							</Field>

							<Field
								label='Email Address'
								validationState={
									formik.touched.emailAddress &&
									formik.errors.emailAddress
										? 'error'
										: 'none'
								}
								validationMessage={formik.errors.emailAddress}
							>
								<Input
									id='emailAddress'
									name='emailAddress'
									value={formik.values.emailAddress}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									placeholder='Required'
									contentBefore={<MailRegular />}
									size='large'
								/>
							</Field>

							<Field
								label='Password'
								validationState={
									formik.touched.password &&
									formik.errors.password
										? 'error'
										: 'none'
								}
								validationMessage={formik.errors.password}
							>
								<Input
									id='password'
									name='password'
									value={formik.values.password}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									placeholder='Required'
									contentBefore={<PasswordRegular />}
									size='large'
									type='password'
								/>
							</Field>

							<div
								style={{
									display: 'flex',
									justifyContent: 'space-between',
									flexWrap: 'wrap'
								}}
							>
								<Field
									label='First Name'
									validationState={
										formik.touched.firstName &&
										formik.errors.firstName
											? 'error'
											: 'none'
									}
									validationMessage={formik.errors.firstName}
									style={{ width: '49%', minWidth: '200px' }}
								>
									<Input
										id='firstName'
										name='firstName'
										value={formik.values.firstName}
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
										placeholder='Optional'
										size='large'
									/>
								</Field>

								<Field
									label='Last Name'
									validationState={
										formik.touched.lastName &&
										formik.errors.lastName
											? 'error'
											: 'none'
									}
									validationMessage={formik.errors.lastName}
									style={{ width: '49%', minWidth: '200px' }}
								>
									<Input
										id='lastName'
										name='lastName'
										value={formik.values.lastName}
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
										placeholder='Optional'
										size='large'
									/>
								</Field>
							</div>

							<Field label='Birthday'>
								<Input
									id='birthday'
									name='birthday'
									value={formik.values.birthday}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									placeholder='Optional'
									size='large'
									type='date'
								/>
							</Field>
						</form>
					</DialogContent>

					<SignUpActions
						isLoading={
							formik.isSubmitting || addingPlayer || loggingIn
						}
						toggleModal={props.toggleModal}
						submitForm={formik.submitForm}
					/>
				</DialogBody>
			</DialogSurface>
		</Dialog>
	);
}

const useStyles = makeStyles({
	field: {
		'& .fui-Field__validationMessage': {
			display: 'flex',
			alignItems: 'center'
		}
	},
	mobile: {
		height: '100%'
	}
});
